import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ChevronLeft } from "@/assets/icons/ds";
import { Button } from "@/components/Interface/Button/Button";
import { CardContainer } from "@/components/Layout/BaseLayouts/CardContainer";
import { getIconForProductCategory } from "@/components/Portal/components/PortalDeviceTiles/TilesGrid/Tiles/DeviceProductCard/ProductCategoryIcon.tsx";
import { SimStatusChip } from "@/components/Portal/components/PortalDeviceTiles/TilesGrid/Tiles/DeviceProductCard/SimStatusChip";
import { SimCardDetail } from "@/services/model";

/**
 * Header of the device detail view: a way back to the subscription list, the
 * product icon, the device name, and its status.
 *
 * The icon sits beside the name from the tablet breakpoint up and above it on
 * mobile, where the name needs the full width.
 */
export const Header = ({ simDetail }: { simDetail: SimCardDetail }) => {
  const { t } = useTranslation("portal");
  const navigate = useNavigate();

  return (
    // All spacing (horizontal inset, top offset, gap to the tabs) comes from
    // TabLayout's header slot — the header card sits flush at the top of
    // the content column, like the Documents and Account headers. The section
    // only carries the semantic landmark and full width.
    <section className="w-full">
      <CardContainer
        className="flex w-full flex-col gap-gutter rounded-lg p-gutter"
        variant="ivory"
      >
        <Button
          dataTestid="detail-view-back"
          variant="tertiary"
          ariaLabel={t("common.buttons.back")}
          icon={<ChevronLeft />}
          onClick={() => navigate(-1)}
        />
        <div className="flex w-full flex-col gap-gutter tablet:flex-row tablet:items-end">
          <div className="flex size-24 shrink-0 items-center justify-center self-end rounded-lg bg-surface-primary-inverse tablet:self-auto">
            <div className="size-14">
              {getIconForProductCategory(simDetail)}
            </div>
          </div>
          <div className="flex min-w-0 flex-1 flex-col items-start gap-2">
            <h1
              data-testid="detail-view-title"
              className="w-full text-headline-xl font-semibold leading-xs tracking-[-0.02em] text-foreground-primary"
            >
              {simDetail.alias}
            </h1>
            <SimStatusChip
              status={simDetail.simStatus}
              numberPorting={simDetail.numberPorting}
            />
          </div>
        </div>
      </CardContainer>
    </section>
  );
};
